import React, { FC, ReactNode } from "react";
import {
  Box,
  Dialog,
  DialogProps,
  DialogTitle,
  Drawer,
  IconButton,
  InternalStandardProps as StandardProps,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery } from "react-responsive";
import { ScreenSize } from "@constants/screen-size";
import { ModalProps } from "@mui/material/Modal";

interface StyledDialogProps extends StandardProps<ModalProps> {
  height?: string;
  title?: string;
  onClose: () => void;
  maxWidth?: DialogProps["maxWidth"];
}

const Content: FC<{
  height?: string;
  title?: string;
  onClose: () => void;
  children: ReactNode;
}> = ({ height, title, children, onClose }) => {
  const isSmScreen = useMediaQuery({
    query: `(max-width: ${ScreenSize.md}px)`,
  });

  return (
    <Box
      minHeight={height}
      maxHeight={height}
      display="flex"
      flexDirection="column"
      padding={isSmScreen ? "10px" : "24px"}
      overflow="auto"
    >
      <Box marginBottom="16px">
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogTitle sx={{ padding: 0 }}>
        {title && (
          <Typography
            fontSize="32px"
            lineHeight="48px"
            fontWeight="400"
            color="#323743FF"
            marginBottom="24px"
          >
            {title}
          </Typography>
        )}
      </DialogTitle>
      {children}
    </Box>
  );
};

const StyledDialog: FC<StyledDialogProps & StandardProps<ModalProps>> = ({
  onClose,
  height,
  title,
  children,
  maxWidth,
  ...props
}) => {
  const isSmScreen = useMediaQuery({
    query: `(max-width: ${ScreenSize.md}px)`,
  });

  return isSmScreen ? (
    <Drawer anchor="bottom" onClose={onClose} {...props}>
      <Content
        onClose={onClose}
        height={height}
        title={title}
        children={children}
      />
    </Drawer>
  ) : (
    <Dialog onClose={onClose} maxWidth={maxWidth || "sm"} fullWidth {...props}>
      <Content
        onClose={onClose}
        height={height}
        title={title}
        children={children}
      />
    </Dialog>
  );
};

export default StyledDialog;
